import { useQuery } from '@tanstack/react-query'
import { History } from 'lucide-react'
import { waApi, type WaMessage } from '../../api/whatsapp'
import { Modal } from '../../components/ui/Modal'
import { PageSpinner } from '../../components/ui/Spinner'
import { fullName } from '../../lib/utils'

/**
 * Every stored version of an edited message, oldest first.
 *
 * Only administrators and company owners can open this — the API refuses other
 * roles, and the action that opens it is hidden for them.
 */
export function MessageHistoryModal({ companyId, chatId, message, onClose }: {
  companyId: string
  chatId: string
  message: WaMessage
  onClose: () => void
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['wa-message-edits', companyId, chatId, message.waMessageId],
    queryFn: () => waApi.messageEdits(companyId, chatId, message.waMessageId),
  })

  const versions = data?.versions ?? []

  return (
    <Modal open onClose={onClose} title="Edit history" size="md">
      <div className="p-4">
        {isLoading && <PageSpinner />}
        {error && (
          <p className="text-sm text-red-500">
            {(error as any)?.response?.data?.message ?? 'Could not load the edit history.'}
          </p>
        )}

        {!isLoading && !error && (
          <>
            <p className="mb-3 flex items-center gap-1.5 text-xs text-gray-500">
              <History className="h-3.5 w-3.5" />
              {data?.editCount === 1 ? 'Edited once' : `Edited ${data?.editCount} times`}
            </p>

            <ol className="space-y-3">
              {versions.map((v, i) => {
                const current = i === versions.length - 1
                return (
                  <li
                    key={v.version}
                    className={`rounded-lg border p-3 ${current ? 'border-green-300 bg-green-50/60' : 'border-gray-200 bg-gray-50'}`}
                  >
                    <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                      <span className={current ? 'font-medium text-green-700' : 'text-gray-500'}>
                        {current ? 'Current version' : `Version ${v.version}`}
                      </span>
                      {v.replacedAt && (
                        <span className="text-gray-400">
                          Replaced {new Date(v.replacedAt).toLocaleString()}
                        </span>
                      )}
                    </div>
                    <p className="whitespace-pre-wrap break-words text-sm text-gray-800">
                      {v.text || <span className="italic text-gray-400">(empty)</span>}
                    </p>
                    {v.replacedAt && (
                      <p className="mt-1 text-[11px] text-gray-400">
                        {v.replacedBy
                          ? `Edited by ${fullName(v.replacedBy.firstName, v.replacedBy.lastName)}`
                          : 'Edited on WhatsApp (outside the CRM)'}
                      </p>
                    )}
                  </li>
                )
              })}
            </ol>
          </>
        )}

        <div className="flex justify-end pt-4">
          <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>
    </Modal>
  )
}
